import { useEffect, useRef, useState } from "react";
import { AppTopBar } from "@/components/AppTopBar";
import ReturnPhysicalIntake from "@/components/ReturnPhysicalIntake";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ReturnManagement() {
  const [cases, setCases] = useState([]);
  const [returnAwbSearch, setReturnAwbSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const closeReceivable = async () => {
    if (!selected || selected.intake_source !== "PHYSICAL_V1" || selected.status !== "PHYSICAL_COMPLETED") return;
    setBusy(true);
    try {
      const { data } = await api.post(`/returns/physical-process/${selected.id}/close-receivable`);
      setSelected(data.case || selected);
      toast.success(data.receivable_action?.reason === "ALREADY_CLOSED" ? "Piutang sudah CLOSED." : "Piutang berhasil ditutup.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Piutang belum dapat ditutup.");
    } finally { setBusy(false); }
  };
  const [corr, setCorr] = useState({ lineId: "", qtyReceived: 0, qtyAccepted: 0, condition: "GOOD", reason: "" });
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/returns");
      setCases(data);
    } catch {
      toast.error("Gagal memuat data retur");
    }
  };
  useEffect(() => { load(); }, []);

  const openCase = async (id) => {
    try {
      const { data } = await api.get(`/returns/${id}`);
      setSelected(data);
      const f = {};
      data.lines.forEach((l) => {
        f[l.id] = {
          qty_received: l.qty_received ?? l.qty_expected,
          qty_accepted: l.qty_accepted ?? l.qty_expected,
          condition: l.condition || "GOOD",
        };
      });
      setForm(f);
      setCorr({ lineId: "", qtyReceived: 0, qtyAccepted: 0, condition: "GOOD", reason: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Gagal membuka retur");
    }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/returns/import/return-refund", fd);
      toast.success(`Import: ${data.return_cases_created} case baru, ${data.inspection_lines_created} line, ${data.skipped_existing} sudah ada`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Import gagal");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submitInspection = async () => {
    if (!selected) return;
    const lines = selected.lines.filter((l) => !l.inspected).map((l) => ({
      line_id: l.id,
      qty_received: Number(form[l.id]?.qty_received ?? 0),
      qty_accepted: Number(form[l.id]?.qty_accepted ?? 0),
      condition: form[l.id]?.condition || "GOOD",
    }));
    if (lines.length === 0) { toast.info("Semua line sudah diinspeksi"); return; }
    setBusy(true);
    try {
      const endpoint = selected.intake_source === "PHYSICAL_V1"
        ? `/returns/physical-process/${selected.id}/inspect`
        : `/returns/${selected.id}/inspect`;
      const { data } = await api.post(endpoint, { lines });
      setSelected(data);
      toast.success("Inspeksi tersimpan & stock-in diproses. Piutang tidak ditutup otomatis.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Inspeksi gagal");
    } finally {
      setBusy(false);
    }
  };

  const chooseCorrectionLine = (lineId) => {
    const line = selected?.lines?.find((x) => x.id === lineId);
    if (!line) {
      setCorr({ lineId: "", qtyReceived: 0, qtyAccepted: 0, condition: "GOOD", reason: "" });
      return;
    }
    setCorr({
      lineId,
      qtyReceived: line.qty_received ?? 0,
      qtyAccepted: line.qty_accepted ?? 0,
      condition: line.condition || "GOOD",
      reason: "",
    });
  };

  const submitCorrection = async () => {
    if (!selected || selected.intake_source !== "PHYSICAL_V1") return;
    if (!corr.lineId) { toast.error("Pilih SKU yang akan dikoreksi"); return; }
    if (!corr.reason.trim()) { toast.error("Alasan koreksi wajib diisi"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/returns/physical-process/lines/${corr.lineId}/correct`, {
        correction_key: `RTN-CORR-${corr.lineId}-${Date.now()}`,
        qty_received: Number(corr.qtyReceived || 0),
        qty_accepted: Number(corr.qtyAccepted || 0),
        condition: corr.condition,
        reason: corr.reason.trim(),
      });
      if (data.case) setSelected(data.case);
      setCorr({ lineId: "", qtyReceived: 0, qtyAccepted: 0, condition: "GOOD", reason: "" });
      toast.success("Koreksi retur tersimpan dengan audit trail");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Koreksi retur gagal");
    } finally {
      setBusy(false);
    }
  };

  const setF = (id, patch) => setForm((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  const inspectedLines = selected?.intake_source === "PHYSICAL_V1" ? selected.lines.filter((x) => x.inspected) : [];
  // Display-only filtering of already-loaded return cases; no new API or mutations.
  const returnAwbQuery = returnAwbSearch.trim().toLowerCase();
  const visibleReturnCases = returnAwbQuery
    ? cases.filter((c) => String(c.return_awb || "").toLowerCase().includes(returnAwbQuery))
    : cases;

  return (
    <div>
      <AppTopBar active="returns" />
      <main className="page" style={{ maxWidth: 1320, margin: "0 auto" }}>
        <div className="page-head">
          <h1 className="page-title">Retur Pembeli</h1>
          <label className="btn-primary" data-testid="import-return-button" style={{ cursor: "pointer" }}>
            {busy ? "Memproses..." : "Import Return/Refund"}
            <input ref={fileRef} type="file" accept=".xls,.xlsx" hidden onChange={doImport} disabled={busy} />
          </label>
        </div>

        <ReturnPhysicalIntake onChanged={load} />

        <section className="panel" data-testid="returns-list-panel">
          <div className="panel-head">
            <div>
              <h2 className="panel-title">Antrian Retur ({cases.length})</h2>
              <div className="muted">Buka case untuk cek fisik. Retur fisik tidak otomatis mengubah piutang/settlement.</div>
            </div>
            <input
              type="search"
              className="field-input"
              aria-label="Cari resi retur dalam antrian"
              data-testid="returns-awb-search"
              placeholder="Cari nomor resi retur..."
              value={returnAwbSearch}
              onChange={(e) => setReturnAwbSearch(e.target.value)}
              style={{ width: 360, maxWidth: "100%", flex: "0 1 360px", minWidth: 200, margin: 0 }}
            />
          </div>
          <div className="table-scroll">
            <table className="data-table" data-testid="returns-table">
              <thead><tr><th>Return / Case</th><th>Order</th><th>Return AWB</th><th>Status</th><th>SKU</th><th>Aksi</th></tr></thead>
              <tbody>
                {visibleReturnCases.length === 0 && <tr><td colSpan={6} className="empty-cell">{cases.length === 0 ? "Belum ada data retur." : "Resi retur tidak ditemukan."}</td></tr>}
                {visibleReturnCases.map((c) => (
                  <tr key={c.id} data-testid={`return-row-${c.return_id}`}>
                    <td className="mono">{c.return_id}</td>
                    <td className="mono">{c.source_order_id || "-"}</td>
                    <td className="mono">{c.return_awb}</td>
                    <td><span className={`status-pill ${c.status?.includes("MATCHED") || c.status === "RETURN_RECEIPT_EVIDENCE" || c.status === "PHYSICAL_COMPLETED" ? "on" : "off"}`}>{c.status}</span></td>
                    <td>{c.lines.length}</td>
                    <td><button className="btn-mini" onClick={() => openCase(c.id)}>Buka / Inspeksi</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {selected && (
          <section className="panel" data-testid="inspection-panel">
            <div className="page-head">
              <div>
                <h2 className="panel-title">Cek Fisik — {selected.return_awb}</h2>
                <div className="muted">Order: <span className="mono">{selected.source_order_id || "UNMATCHED"}</span></div>
                {selected.intake_source === "PHYSICAL_V1" && selected.status === "PHYSICAL_COMPLETED" && (
                  <button className="btn-primary" onClick={closeReceivable} disabled={busy} data-testid="physical-close-receivable">
                    {busy ? "Memproses..." : "Tutup Piutang"}
                  </button>
                )}
              </div>
              <button className="btn-ghost" onClick={() => setSelected(null)}>Tutup</button>
            </div>
            <div className="table-scroll">
              <table className="data-table">
                <thead><tr><th>SKU</th><th>Qty Referensi</th><th>Qty Fisik</th><th>Qty Masuk</th><th>Kondisi</th><th>Hasil</th></tr></thead>
                <tbody>
                  {selected.lines.map((l) => (
                    <tr key={l.id}>
                      <td className="mono">{l.marketplace_sku}</td>
                      <td>{l.qty_expected}</td>
                      <td>{l.inspected ? l.qty_received : <input type="number" min={0} style={{ width: 80 }} value={form[l.id]?.qty_received ?? 0} onChange={(e) => setF(l.id, { qty_received: e.target.value })} />}</td>
                      <td>{l.inspected ? l.qty_accepted : <input type="number" min={0} style={{ width: 80 }} value={form[l.id]?.qty_accepted ?? 0} onChange={(e) => setF(l.id, { qty_accepted: e.target.value })} />}</td>
                      <td>{l.inspected ? l.condition : <select value={form[l.id]?.condition || "GOOD"} onChange={(e) => setF(l.id, { condition: e.target.value })}><option value="GOOD">GOOD / Layak</option><option value="DAMAGED">DAMAGED / Rusak</option><option value="WRONG_ITEM">WRONG / Barang Salah</option><option value="EMPTY">EMPTY / Kosong</option><option value="OTHER">HOLD / Lainnya</option></select>}</td>
                      <td>{l.inspected ? <span className={`status-pill ${l.stock_in_done ? "on" : "off"}`}>{l.stock_in_done ? `IN ${l.stock_in_bucket}` : (l.exception_flag || "NO STOCK-IN")}</span> : <span className="empty-cell">BELUM DIPROSES</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {selected.lines.some((x) => !x.inspected) && (
              <div style={{ marginTop: 12 }}>
                <button className="btn-primary" onClick={submitInspection} disabled={busy}>Proses Inspeksi &amp; Stock-In</button>
              </div>
            )}

            {inspectedLines.length > 0 && (
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #ddd" }}>
                <h3 style={{ margin: "0 0 8px" }}>Koreksi Setelah Stock-In</h3>
                <div className="muted" style={{ marginBottom: 12 }}>Pakai hanya jika admin salah input. Histori lama tetap disimpan dan koreksi dicatat terpisah.</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
                  <div style={{ minWidth: 220 }}><label className="field-label">SKU</label><select className="field-input" value={corr.lineId} onChange={(e) => chooseCorrectionLine(e.target.value)}><option value="">Pilih SKU</option>{inspectedLines.map((l) => <option key={l.id} value={l.id}>{l.marketplace_sku} — {l.condition}</option>)}</select></div>
                  <div><label className="field-label">Qty Fisik</label><input className="field-input" type="number" min={0} style={{ width: 90 }} value={corr.qtyReceived} onChange={(e) => setCorr({ ...corr, qtyReceived: e.target.value })} /></div>
                  <div><label className="field-label">Qty Masuk</label><input className="field-input" type="number" min={0} style={{ width: 90 }} value={corr.qtyAccepted} onChange={(e) => setCorr({ ...corr, qtyAccepted: e.target.value })} /></div>
                  <div style={{ minWidth: 190 }}><label className="field-label">Kondisi</label><select className="field-input" value={corr.condition} onChange={(e) => setCorr({ ...corr, condition: e.target.value })}><option value="GOOD">GOOD / Layak</option><option value="DAMAGED">DAMAGED / Rusak</option><option value="WRONG_ITEM">WRONG / Barang Salah</option><option value="EMPTY">EMPTY / Kosong</option><option value="OTHER">HOLD / Lainnya</option></select></div>
                  <div style={{ flex: 1, minWidth: 280 }}><label className="field-label">Alasan Koreksi *</label><input className="field-input" value={corr.reason} onChange={(e) => setCorr({ ...corr, reason: e.target.value })} placeholder="Contoh: qty fisik seharusnya 1" /></div>
                  <button className="btn-primary" style={{ width: "auto" }} onClick={submitCorrection} disabled={busy || !corr.lineId}>{busy ? "Memproses..." : "Proses Koreksi"}</button>
                </div>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
